export interface PharmacyService {
  id: string;
  label: string;
  icon: string;
  color: string;
}

export interface PharmacySchedule {
  day: string;
  open: string;
  close: string;
  isToday?: boolean;
}

export interface PharmacyReview {
  id: number;
  author: string;
  avatar: string;
  rating: number;
  date: string;
  text: string;
  helpful: number;
}

export interface Pharmacy {
  id: number;
  name: string;
  address: string;
  district: string;
  metro: string;
  metroLine: string;
  metroColor: string;
  metroExit: string;
  phone: string;
  hours: string;
  is24h: boolean;
  isOpen: boolean;
  rating: number;
  reviews: number;
  distance: string;
  services: string[];
  schedule: PharmacySchedule[];
  image: string;
  gallery: string[];
  coords: { lat: number; lng: number };
  mapEmbedUrl: string;
  tags: string[];
  hasDelivery: boolean;
  hasReservation: boolean;
  hasDriveThru: boolean;
  description: string;
  howToGet: string;
  paymentMethods: string[];
  features: { icon: string; label: string; value: string }[];
  reviewsList: PharmacyReview[];
  nearbyPharmacies: number[];
}

export const PHARMACY_SERVICES: PharmacyService[] = [
  { id: 'delivery', label: 'Доставка', icon: 'ri-truck-line', color: '#ff2d78' },
  { id: 'reservation', label: 'Бронирование', icon: 'ri-bookmark-line', color: '#00f5ff' },
  { id: 'consultation', label: 'Консультация', icon: 'ri-stethoscope-line', color: '#39ff14' },
  { id: 'pressure', label: 'Измерение давления', icon: 'ri-heart-pulse-line', color: '#f0ff00' },
  { id: 'injection', label: 'Инъекции', icon: 'ri-syringe-line', color: '#ff6b00' },
  { id: 'dressing', label: 'Перевязки', icon: 'ri-first-aid-kit-line', color: '#ff2d78' },
  { id: 'drive', label: 'Drive-thru', icon: 'ri-car-line', color: '#39ff14' },
  { id: 'night', label: 'Ночная', icon: 'ri-moon-line', color: '#00f5ff' },
];

const today = new Date().getDay();
const dayNames = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

const makeSchedule = (weekOpen: string, weekClose: string, weekendOpen: string, weekendClose: string): PharmacySchedule[] =>
  dayNames.map((day, i) => ({
    day,
    open: (i === 0 || i === 6) ? weekendOpen : weekOpen,
    close: (i === 0 || i === 6) ? weekendClose : weekClose,
    isToday: i === today,
  }));

const makeSchedule24 = (): PharmacySchedule[] =>
  dayNames.map((day, i) => ({ day, open: '00:00', close: '24:00', isToday: i === today }));

const COMMON_REVIEWS: PharmacyReview[] = [
  { id: 1, author: 'Анна К.', avatar: 'https://public.readdy.ai/ai/img_res/09e9a94a74f8cf6556b3d30e4510b5af.jpg', rating: 5, date: '12 марта 2026', text: 'Отличная аптека! Большой выбор, вежливый персонал. Нашла редкий препарат, которого не было в других аптеках. Буду приходить сюда постоянно.', helpful: 24 },
  { id: 2, author: 'Михаил Д.', avatar: 'https://readdy.ai/api/search-image?query=middle%20aged%20man%20portrait%20professional%20photo%20neutral%20background%20minimal&width=80&height=80&seq=av2&orientation=squarish', rating: 5, date: '5 марта 2026', text: 'Удобное расположение, рядом с метро. Фармацевт помог подобрать аналог дорогого препарата — сэкономил приличную сумму. Спасибо!', helpful: 18 },
  { id: 3, author: 'Елена В.', avatar: 'https://readdy.ai/api/search-image?query=woman%2040s%20portrait%20professional%20photo%20neutral%20background%20minimal&width=80&height=80&seq=av3&orientation=squarish', rating: 4, date: '28 февраля 2026', text: 'Хорошая аптека, всё есть в наличии. Единственный минус — иногда очередь в часы пик. В целом довольна, рекомендую.', helpful: 11 },
  { id: 4, author: 'Сергей П.', avatar: 'https://readdy.ai/api/search-image?query=young%20man%20portrait%20professional%20photo%20neutral%20background%20minimal&width=80&height=80&seq=av4&orientation=squarish', rating: 5, date: '20 февраля 2026', text: 'Заказал через приложение, забрал через 15 минут. Всё быстро и удобно. Цены приятные, есть скидки по карте лояльности.', helpful: 32 },
  { id: 5, author: 'Ольга М.', avatar: 'https://readdy.ai/api/search-image?query=woman%2030s%20portrait%20professional%20photo%20neutral%20background%20minimal&width=80&height=80&seq=av5&orientation=squarish', rating: 4, date: '14 февраля 2026', text: 'Чисто, светло, приятная атмосфера. Персонал грамотный, ответили на все вопросы. Измерили давление бесплатно — очень удобно.', helpful: 15 },
  { id: 6, author: 'Дмитрий Н.', avatar: 'https://readdy.ai/api/search-image?query=man%2050s%20portrait%20professional%20photo%20neutral%20background%20minimal&width=80&height=80&seq=av6&orientation=squarish', rating: 5, date: '8 февраля 2026', text: 'Регулярно хожу сюда уже 2 года. Всегда есть нужные препараты, цены стабильные. Фармацевты знают своё дело — всегда дадут дельный совет.', helpful: 41 },
];

export const pharmacies: Pharmacy[] = [
  {
    id: 1,
    name: 'NeonPharma Арбат',
    address: 'ул. Арбат, 12, Москва',
    district: 'ЦАО',
    metro: 'Арбатская',
    metroLine: 'Арбатско-Покровская линия',
    metroColor: '#0078be',
    metroExit: 'Выход 3, затем 2 минуты пешком по ул. Арбат в сторону Смоленской площади',
    phone: '+7 (495) 123-45-01',
    hours: '08:00–22:00',
    is24h: false,
    isOpen: true,
    rating: 4.9,
    reviews: 847,
    distance: '0.3 км',
    services: ['delivery', 'reservation', 'consultation', 'pressure'],
    schedule: makeSchedule('08:00', '22:00', '09:00', '21:00'),
    image: 'https://readdy.ai/api/search-image?query=modern%20neon%20pharmacy%20interior%20glowing%20pink%20signs%20dark%20cyberpunk%20aesthetic%20clean%20minimalist%20shelves%20medicine%20bottles%20night%20city%20Moscow%20street%20view&width=600&height=400&seq=ph1&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=modern%20pharmacy%20interior%20neon%20pink%20glow%20dark%20shelves%20medicine%20bottles%20clean%20minimalist%20Moscow&width=800&height=500&seq=ph1g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20counter%20reception%20neon%20glow%20dark%20cyberpunk%20aesthetic%20clean%20modern%20interior&width=800&height=500&seq=ph1g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20shelves%20vitamins%20supplements%20neon%20glow%20dark%20background%20clean%20organized%20display&width=800&height=500&seq=ph1g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20entrance%20storefront%20neon%20sign%20night%20Moscow%20Arbat%20street%20glowing%20pink&width=800&height=500&seq=ph1g4&orientation=landscape',
    ],
    coords: { lat: 55.7520, lng: 37.5920 },
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2245.8!2d37.5920!3d55.7520!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46b54a50b315e573%3A0xa886bf5a3d9b2e68!2z0JzQvtGB0LrQstCw!5e0!3m2!1sru!2sru!4v1700000000000!5m2!1sru!2sru',
    tags: ['Центр', 'Рядом с метро', 'Большой выбор'],
    hasDelivery: true,
    hasReservation: true,
    hasDriveThru: false,
    description: 'Флагманская аптека NeonPharma на Арбате — одна из крупнейших в центре Москвы. Более 5 000 наименований препаратов, витаминов и медицинских товаров. Опытные фармацевты с высшим фармацевтическим образованием готовы помочь с выбором препарата и ответить на любые вопросы.',
    howToGet: 'Метро Арбатская (Арбатско-Покровская линия), выход 3. Идти по ул. Арбат в сторону Смоленской площади около 2 минут. Аптека находится на первом этаже жилого дома, вход со стороны улицы. Ориентир — кофейня «Шоколадница».',
    paymentMethods: ['Наличные', 'Банковская карта', 'СБП', 'Apple Pay', 'Google Pay', 'Карта лояльности NeonPharma'],
    features: [
      { icon: 'ri-store-2-line', label: 'Площадь', value: '180 м²' },
      { icon: 'ri-medicine-bottle-line', label: 'Ассортимент', value: '5 000+ позиций' },
      { icon: 'ri-parking-line', label: 'Парковка', value: 'Платная рядом' },
      { icon: 'ri-wheelchair-line', label: 'Доступность', value: 'Пандус, лифт' },
    ],
    reviewsList: COMMON_REVIEWS,
    nearbyPharmacies: [7, 4, 2],
  },
  {
    id: 2,
    name: 'NeonPharma Тверская',
    address: 'ул. Тверская, 7, Москва',
    district: 'ЦАО',
    metro: 'Тверская',
    metroLine: 'Замоскворецкая линия',
    metroColor: '#0078be',
    metroExit: 'Выход 5, аптека в 1 минуте ходьбы по Тверской улице',
    phone: '+7 (495) 123-45-02',
    hours: '24/7',
    is24h: true,
    isOpen: true,
    rating: 4.8,
    reviews: 1243,
    distance: '0.7 км',
    services: ['delivery', 'reservation', 'consultation', 'pressure', 'injection', 'night'],
    schedule: makeSchedule24(),
    image: 'https://readdy.ai/api/search-image?query=neon%20pharmacy%20storefront%20night%20glowing%20sign%20cyberpunk%20dark%20street%20Moscow%20Tverskaya%20illuminated%20modern%20design%20pink%20cyan%20lights&width=600&height=400&seq=ph2&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=24%20hour%20pharmacy%20interior%20night%20neon%20glow%20dark%20cyberpunk%20aesthetic%20clean%20modern%20Moscow%20Tverskaya&width=800&height=500&seq=ph2g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20night%20shift%20interior%20neon%20lights%20dark%20aesthetic%20clean%20shelves%20medicine&width=800&height=500&seq=ph2g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20injection%20room%20medical%20neon%20glow%20dark%20clean%20sterile%20modern&width=800&height=500&seq=ph2g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20storefront%20Tverskaya%20Moscow%20night%20neon%20sign%20glowing%20pink%20cyan%20dark%20street&width=800&height=500&seq=ph2g4&orientation=landscape',
    ],
    coords: { lat: 55.7650, lng: 37.6050 },
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2244.1!2d37.6050!3d55.7650!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46b54a50b315e573%3A0xa886bf5a3d9b2e68!2z0JzQvtGB0LrQstCw!5e0!3m2!1sru!2sru!4v1700000000001!5m2!1sru!2sru',
    tags: ['24/7', 'Центр', 'Инъекции'],
    hasDelivery: true,
    hasReservation: true,
    hasDriveThru: false,
    description: 'Круглосуточная аптека NeonPharma на Тверской — работает без выходных и праздников. Полный спектр медицинских услуг: инъекции, измерение давления, консультации фармацевта. Один из самых больших ассортиментов в центре Москвы.',
    howToGet: 'Метро Тверская (Замоскворецкая линия), выход 5. Аптека находится в 1 минуте ходьбы по Тверской улице в сторону Кремля. Вход с улицы, работает круглосуточно.',
    paymentMethods: ['Наличные', 'Банковская карта', 'СБП', 'Apple Pay', 'Google Pay', 'Карта лояльности NeonPharma'],
    features: [
      { icon: 'ri-store-2-line', label: 'Площадь', value: '240 м²' },
      { icon: 'ri-medicine-bottle-line', label: 'Ассортимент', value: '7 000+ позиций' },
      { icon: 'ri-parking-line', label: 'Парковка', value: 'Платная рядом' },
      { icon: 'ri-wheelchair-line', label: 'Доступность', value: 'Пандус' },
    ],
    reviewsList: COMMON_REVIEWS,
    nearbyPharmacies: [1, 4, 7],
  },
  {
    id: 3,
    name: 'NeonPharma Сокол',
    address: 'Ленинградский пр., 45, Москва',
    district: 'САО',
    metro: 'Сокол',
    metroLine: 'Замоскворецкая линия',
    metroColor: '#0078be',
    metroExit: 'Выход 2, идти по Ленинградскому проспекту 3 минуты в сторону центра',
    phone: '+7 (495) 123-45-03',
    hours: '08:00–23:00',
    is24h: false,
    isOpen: true,
    rating: 4.7,
    reviews: 562,
    distance: '1.2 км',
    services: ['delivery', 'reservation', 'consultation', 'dressing'],
    schedule: makeSchedule('08:00', '23:00', '09:00', '22:00'),
    image: 'https://readdy.ai/api/search-image?query=pharmacy%20interior%20modern%20neon%20glow%20green%20cyan%20dark%20aesthetic%20clean%20shelves%20medicine%20display%20Moscow%20night%20urban&width=600&height=400&seq=ph3&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=pharmacy%20interior%20neon%20green%20glow%20dark%20shelves%20medicine%20clean%20modern%20Moscow%20Sokol&width=800&height=500&seq=ph3g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20dressing%20room%20medical%20neon%20glow%20dark%20clean%20sterile%20modern%20bandages&width=800&height=500&seq=ph3g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20vitamins%20supplements%20display%20neon%20glow%20dark%20background%20organized%20shelves&width=800&height=500&seq=ph3g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20storefront%20Leningradsky%20prospect%20Moscow%20night%20neon%20sign%20glowing&width=800&height=500&seq=ph3g4&orientation=landscape',
    ],
    coords: { lat: 55.7980, lng: 37.5130 },
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2241.5!2d37.5130!3d55.7980!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46b54a50b315e573%3A0xa886bf5a3d9b2e68!2z0JzQvtGB0LrQstCw!5e0!3m2!1sru!2sru!4v1700000000002!5m2!1sru!2sru',
    tags: ['Перевязки', 'Большой выбор'],
    hasDelivery: true,
    hasReservation: true,
    hasDriveThru: false,
    description: 'Аптека NeonPharma на Соколе специализируется на широком ассортименте перевязочных материалов и медицинских изделий. Профессиональные перевязки выполняются опытными медицинскими работниками. Удобное расположение рядом с метро.',
    howToGet: 'Метро Сокол (Замоскворецкая линия), выход 2. Идти по Ленинградскому проспекту в сторону центра около 3 минут. Аптека на первом этаже торгового центра. Ориентир — вход со стороны проспекта.',
    paymentMethods: ['Наличные', 'Банковская карта', 'СБП', 'Apple Pay', 'Карта лояльности NeonPharma'],
    features: [
      { icon: 'ri-store-2-line', label: 'Площадь', value: '150 м²' },
      { icon: 'ri-medicine-bottle-line', label: 'Ассортимент', value: '4 500+ позиций' },
      { icon: 'ri-parking-line', label: 'Парковка', value: 'Бесплатная в ТЦ' },
      { icon: 'ri-wheelchair-line', label: 'Доступность', value: 'Пандус, лифт' },
    ],
    reviewsList: COMMON_REVIEWS.slice(1),
    nearbyPharmacies: [5, 8, 2],
  },
  {
    id: 4,
    name: 'NeonPharma Таганская',
    address: 'ул. Таганская, 3, Москва',
    district: 'ЦАО',
    metro: 'Таганская',
    metroLine: 'Кольцевая линия',
    metroColor: '#ff5a00',
    metroExit: 'Выход 7, аптека в 2 минутах ходьбы по ул. Таганская',
    phone: '+7 (495) 123-45-04',
    hours: '09:00–21:00',
    is24h: false,
    isOpen: false,
    rating: 4.6,
    reviews: 389,
    distance: '1.8 км',
    services: ['reservation', 'consultation', 'pressure'],
    schedule: makeSchedule('09:00', '21:00', '10:00', '20:00'),
    image: 'https://readdy.ai/api/search-image?query=pharmacy%20storefront%20neon%20sign%20dark%20evening%20Moscow%20street%20urban%20cyberpunk%20aesthetic%20pink%20glow%20modern%20design&width=600&height=400&seq=ph4&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=pharmacy%20interior%20neon%20orange%20glow%20dark%20shelves%20medicine%20clean%20modern%20Moscow%20Taganskaya&width=800&height=500&seq=ph4g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20blood%20pressure%20measurement%20room%20neon%20glow%20dark%20clean%20modern%20medical&width=800&height=500&seq=ph4g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20consultation%20desk%20pharmacist%20neon%20glow%20dark%20clean%20modern%20interior&width=800&height=500&seq=ph4g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20storefront%20Taganskaya%20Moscow%20evening%20neon%20sign%20glowing%20orange&width=800&height=500&seq=ph4g4&orientation=landscape',
    ],
    coords: { lat: 55.7390, lng: 37.6530 },
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2246.9!2d37.6530!3d55.7390!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46b54a50b315e573%3A0xa886bf5a3d9b2e68!2z0JzQvtGB0LrQstCw!5e0!3m2!1sru!2sru!4v1700000000003!5m2!1sru!2sru',
    tags: ['Рядом с метро'],
    hasDelivery: false,
    hasReservation: true,
    hasDriveThru: false,
    description: 'Аптека NeonPharma на Таганской — удобная точка для жителей Таганского района. Квалифицированные фармацевты, измерение артериального давления, бесплатные консультации. Бронирование препаратов онлайн.',
    howToGet: 'Метро Таганская (Кольцевая линия), выход 7. Идти по ул. Таганская около 2 минут. Аптека расположена в цокольном этаже жилого дома. Ориентир — магазин «Пятёрочка».',
    paymentMethods: ['Наличные', 'Банковская карта', 'СБП', 'Карта лояльности NeonPharma'],
    features: [
      { icon: 'ri-store-2-line', label: 'Площадь', value: '90 м²' },
      { icon: 'ri-medicine-bottle-line', label: 'Ассортимент', value: '3 000+ позиций' },
      { icon: 'ri-parking-line', label: 'Парковка', value: 'Бесплатная во дворе' },
      { icon: 'ri-wheelchair-line', label: 'Доступность', value: 'Пандус' },
    ],
    reviewsList: COMMON_REVIEWS.slice(2),
    nearbyPharmacies: [1, 2, 7],
  },
  {
    id: 5,
    name: 'NeonPharma Митино',
    address: 'ул. Митинская, 28, Москва',
    district: 'СЗАО',
    metro: 'Митино',
    metroLine: 'Арбатско-Покровская линия',
    metroColor: '#ef7c00',
    metroExit: 'Выход 1, Drive-thru доступен с ул. Митинская',
    phone: '+7 (495) 123-45-05',
    hours: '08:00–22:00',
    is24h: false,
    isOpen: true,
    rating: 4.7,
    reviews: 421,
    distance: '2.1 км',
    services: ['delivery', 'reservation', 'consultation', 'drive'],
    schedule: makeSchedule('08:00', '22:00', '09:00', '21:00'),
    image: 'https://readdy.ai/api/search-image?query=drive-thru%20pharmacy%20modern%20neon%20glow%20dark%20night%20urban%20Moscow%20suburban%20area%20cyberpunk%20aesthetic%20clean%20design&width=600&height=400&seq=ph5&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=drive-thru%20pharmacy%20window%20car%20service%20neon%20glow%20dark%20night%20modern%20Moscow&width=800&height=500&seq=ph5g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20parking%20lot%20drive-thru%20neon%20sign%20night%20dark%20modern%20suburban%20Moscow&width=800&height=500&seq=ph5g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20interior%20neon%20glow%20dark%20shelves%20medicine%20clean%20modern%20suburban%20Moscow%20Mitino&width=800&height=500&seq=ph5g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20storefront%20Mitino%20Moscow%20night%20neon%20sign%20glowing%20modern%20design&width=800&height=500&seq=ph5g4&orientation=landscape',
    ],
    coords: { lat: 55.8350, lng: 37.3560 },
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2239.8!2d37.3560!3d55.8350!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46b54a50b315e573%3A0xa886bf5a3d9b2e68!2z0JzQvtGB0LrQstCw!5e0!3m2!1sru!2sru!4v1700000000004!5m2!1sru!2sru',
    tags: ['Drive-thru', 'Парковка'],
    hasDelivery: true,
    hasReservation: true,
    hasDriveThru: true,
    description: 'Единственная аптека NeonPharma с Drive-thru в Митино — получите заказ, не выходя из машины! Большая бесплатная парковка, удобный подъезд. Предварительное бронирование через приложение — и ваш заказ будет готов к приезду.',
    howToGet: 'Метро Митино (Арбатско-Покровская линия), выход 1. Аптека в 5 минутах ходьбы по ул. Митинская. Для Drive-thru: въезд с ул. Митинская, окно обслуживания с правой стороны здания. Бесплатная парковка на 30 мест.',
    paymentMethods: ['Наличные', 'Банковская карта', 'СБП', 'Apple Pay', 'Google Pay', 'Карта лояльности NeonPharma'],
    features: [
      { icon: 'ri-store-2-line', label: 'Площадь', value: '200 м²' },
      { icon: 'ri-medicine-bottle-line', label: 'Ассортимент', value: '4 000+ позиций' },
      { icon: 'ri-parking-line', label: 'Парковка', value: 'Бесплатная, 30 мест' },
      { icon: 'ri-wheelchair-line', label: 'Доступность', value: 'Пандус, широкие проходы' },
    ],
    reviewsList: COMMON_REVIEWS.slice(0, 4),
    nearbyPharmacies: [3, 8, 6],
  },
  {
    id: 6,
    name: 'NeonPharma Выхино',
    address: 'ул. Ташкентская, 9, Москва',
    district: 'ЮВАО',
    metro: 'Выхино',
    metroLine: 'Таганско-Краснопресненская линия',
    metroColor: '#ff5a00',
    metroExit: 'Выход 2, аптека в 3 минутах ходьбы по ул. Ташкентская',
    phone: '+7 (495) 123-45-06',
    hours: '24/7',
    is24h: true,
    isOpen: true,
    rating: 4.8,
    reviews: 734,
    distance: '2.5 км',
    services: ['delivery', 'reservation', 'consultation', 'pressure', 'injection', 'night'],
    schedule: makeSchedule24(),
    image: 'https://readdy.ai/api/search-image?query=neon%20pharmacy%2024%20hours%20open%20night%20glowing%20sign%20dark%20street%20Moscow%20eastern%20district%20cyberpunk%20pink%20cyan%20aesthetic&width=600&height=400&seq=ph6&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=24%20hour%20pharmacy%20interior%20night%20neon%20glow%20dark%20cyberpunk%20aesthetic%20clean%20modern%20Moscow%20Vykhino&width=800&height=500&seq=ph6g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20injection%20medical%20room%20neon%20glow%20dark%20clean%20sterile%20modern%20night&width=800&height=500&seq=ph6g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20night%20interior%20neon%20lights%20dark%20aesthetic%20clean%20shelves%20medicine%20eastern%20Moscow&width=800&height=500&seq=ph6g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20storefront%20Vykhino%20Moscow%20night%20neon%20sign%20glowing%2024%20hours%20open&width=800&height=500&seq=ph6g4&orientation=landscape',
    ],
    coords: { lat: 55.7180, lng: 37.8420 },
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2247.5!2d37.8420!3d55.7180!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46b54a50b315e573%3A0xa886bf5a3d9b2e68!2z0JzQvtGB0LrQstCw!5e0!3m2!1sru!2sru!4v1700000000005!5m2!1sru!2sru',
    tags: ['24/7', 'Инъекции'],
    hasDelivery: true,
    hasReservation: true,
    hasDriveThru: false,
    description: 'Круглосуточная аптека NeonPharma в Выхино — полный спектр медицинских услуг в любое время суток. Инъекции, измерение давления, консультации. Один из крупнейших ассортиментов в ЮВАО.',
    howToGet: 'Метро Выхино (Таганско-Краснопресненская линия), выход 2. Идти по ул. Ташкентская около 3 минут. Аптека на первом этаже жилого дома. Работает круглосуточно, вход всегда открыт.',
    paymentMethods: ['Наличные', 'Банковская карта', 'СБП', 'Apple Pay', 'Google Pay', 'Карта лояльности NeonPharma'],
    features: [
      { icon: 'ri-store-2-line', label: 'Площадь', value: '160 м²' },
      { icon: 'ri-medicine-bottle-line', label: 'Ассортимент', value: '5 500+ позиций' },
      { icon: 'ri-parking-line', label: 'Парковка', value: 'Бесплатная во дворе' },
      { icon: 'ri-wheelchair-line', label: 'Доступность', value: 'Пандус' },
    ],
    reviewsList: COMMON_REVIEWS,
    nearbyPharmacies: [4, 3, 5],
  },
  {
    id: 7,
    name: 'NeonPharma Хамовники',
    address: 'Комсомольский пр., 18, Москва',
    district: 'ЦАО',
    metro: 'Фрунзенская',
    metroLine: 'Сокольническая линия',
    metroColor: '#0078be',
    metroExit: 'Выход 1, аптека в 2 минутах ходьбы по Комсомольскому проспекту',
    phone: '+7 (495) 123-45-07',
    hours: '08:00–22:00',
    is24h: false,
    isOpen: true,
    rating: 4.9,
    reviews: 612,
    distance: '0.9 км',
    services: ['delivery', 'reservation', 'consultation', 'pressure', 'dressing'],
    schedule: makeSchedule('08:00', '22:00', '09:00', '21:00'),
    image: 'https://readdy.ai/api/search-image?query=premium%20pharmacy%20interior%20neon%20glow%20pink%20dark%20elegant%20Moscow%20Khamovniki%20district%20modern%20design%20clean%20shelves&width=600&height=400&seq=ph7&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=premium%20pharmacy%20interior%20neon%20pink%20glow%20dark%20elegant%20shelves%20medicine%20clean%20modern%20Moscow%20Khamovniki&width=800&height=500&seq=ph7g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20premium%20consultation%20room%20neon%20glow%20dark%20elegant%20clean%20modern%20interior&width=800&height=500&seq=ph7g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20dressing%20bandage%20room%20neon%20glow%20dark%20clean%20sterile%20modern%20premium&width=800&height=500&seq=ph7g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20storefront%20Komsomolsky%20prospect%20Moscow%20night%20neon%20sign%20glowing%20premium&width=800&height=500&seq=ph7g4&orientation=landscape',
    ],
    coords: { lat: 55.7310, lng: 37.5780 },
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2246.3!2d37.5780!3d55.7310!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46b54a50b315e573%3A0xa886bf5a3d9b2e68!2z0JzQvtGB0LrQstCw!5e0!3m2!1sru!2sru!4v1700000000006!5m2!1sru!2sru',
    tags: ['Центр', 'Премиум', 'Перевязки'],
    hasDelivery: true,
    hasReservation: true,
    hasDriveThru: false,
    description: 'Премиальная аптека NeonPharma в Хамовниках — флагман качества и сервиса. Эксклюзивный ассортимент европейских препаратов и косметики, профессиональные перевязки, персональные консультации. Для клиентов программы лояльности — приоритетное обслуживание.',
    howToGet: 'Метро Фрунзенская (Сокольническая линия), выход 1. Идти по Комсомольскому проспекту в сторону центра около 2 минут. Аптека в отдельно стоящем здании с большими витринами. Ориентир — ресторан «Буфет».',
    paymentMethods: ['Наличные', 'Банковская карта', 'СБП', 'Apple Pay', 'Google Pay', 'Samsung Pay', 'Карта лояльности NeonPharma'],
    features: [
      { icon: 'ri-store-2-line', label: 'Площадь', value: '220 м²' },
      { icon: 'ri-medicine-bottle-line', label: 'Ассортимент', value: '6 000+ позиций' },
      { icon: 'ri-parking-line', label: 'Парковка', value: 'Бесплатная, 15 мест' },
      { icon: 'ri-wheelchair-line', label: 'Доступность', value: 'Пандус, лифт, широкие проходы' },
    ],
    reviewsList: COMMON_REVIEWS,
    nearbyPharmacies: [1, 4, 2],
  },
  {
    id: 8,
    name: 'NeonPharma Бутово',
    address: 'ул. Скобелевская, 4, Москва',
    district: 'ЮЗАО',
    metro: 'Бульвар Дмитрия Донского',
    metroLine: 'Серпуховско-Тимирязевская линия',
    metroColor: '#999999',
    metroExit: 'Выход 3, Drive-thru доступен с ул. Скобелевская',
    phone: '+7 (495) 123-45-08',
    hours: '09:00–21:00',
    is24h: false,
    isOpen: true,
    rating: 4.5,
    reviews: 298,
    distance: '3.4 км',
    services: ['delivery', 'reservation', 'drive'],
    schedule: makeSchedule('09:00', '21:00', '10:00', '20:00'),
    image: 'https://readdy.ai/api/search-image?query=pharmacy%20drive-thru%20suburban%20Moscow%20neon%20sign%20night%20dark%20modern%20design%20green%20glow%20clean%20minimal&width=600&height=400&seq=ph8&orientation=landscape',
    gallery: [
      'https://readdy.ai/api/search-image?query=drive-thru%20pharmacy%20window%20car%20service%20neon%20glow%20dark%20night%20modern%20suburban%20south%20Moscow%20Butovo&width=800&height=500&seq=ph8g1&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20parking%20lot%20suburban%20Moscow%20neon%20sign%20night%20dark%20modern%20design&width=800&height=500&seq=ph8g2&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20interior%20neon%20glow%20dark%20shelves%20medicine%20clean%20modern%20suburban%20south%20Moscow&width=800&height=500&seq=ph8g3&orientation=landscape',
      'https://readdy.ai/api/search-image?query=pharmacy%20storefront%20Butovo%20Moscow%20night%20neon%20sign%20glowing%20modern%20design%20suburban&width=800&height=500&seq=ph8g4&orientation=landscape',
    ],
    coords: { lat: 55.5720, lng: 37.5890 },
    mapEmbedUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2253.1!2d37.5890!3d55.5720!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46b54a50b315e573%3A0xa886bf5a3d9b2e68!2z0JzQvtGB0LrQstCw!5e0!3m2!1sru!2sru!4v1700000000007!5m2!1sru!2sru',
    tags: ['Drive-thru', 'Парковка', 'Юг Москвы'],
    hasDelivery: true,
    hasReservation: true,
    hasDriveThru: true,
    description: 'Аптека NeonPharma в Бутово с удобным Drive-thru — идеально для жителей юга Москвы. Большая бесплатная парковка, быстрое обслуживание. Онлайн-бронирование и самовывоз без очереди.',
    howToGet: 'Метро Бульвар Дмитрия Донского (Серпуховско-Тимирязевская линия), выход 3. Аптека в 7 минутах ходьбы по ул. Скобелевская. Drive-thru: въезд с ул. Скобелевская, окно обслуживания слева. Бесплатная парковка на 25 мест.',
    paymentMethods: ['Наличные', 'Банковская карта', 'СБП', 'Apple Pay', 'Карта лояльности NeonPharma'],
    features: [
      { icon: 'ri-store-2-line', label: 'Площадь', value: '130 м²' },
      { icon: 'ri-medicine-bottle-line', label: 'Ассортимент', value: '3 500+ позиций' },
      { icon: 'ri-parking-line', label: 'Парковка', value: 'Бесплатная, 25 мест' },
      { icon: 'ri-wheelchair-line', label: 'Доступность', value: 'Пандус' },
    ],
    reviewsList: COMMON_REVIEWS.slice(1, 5),
    nearbyPharmacies: [5, 3, 6],
  },
];

export const DISTRICTS = ['Все районы', 'ЦАО', 'САО', 'СЗАО', 'ЮВАО', 'ЮЗАО'];
